import { FC } from 'react';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { t } from 'i18next';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { Button } from 'components/_basic/Button/Button';

import { CONFIRM_BUTTON_COLOR_TYPE } from './constants';
import { IConfirmModalProps } from './types';
import { Modal } from '../Modal';

import styles from './ConfirmModal.module.scss';

export const ConfirmModal: FC<IConfirmModalProps> = ({
  title,
  body,
  onAbort,
  onConfirm,
  confirmText = t('delete'),
  abortText = t('cancel'),
  confirmButtonColor = CONFIRM_BUTTON_COLOR_TYPE.ERROR,
  showCloseButton = true,
  showAbortButton = true,
  fullWidthButtons = false,
  className = '',
  maxWidth = 'xs',
}) => {
  const { track, isPending } = usePromiseStatus();

  const onConfirmClick = async () => {
    const result = onConfirm();
    result instanceof Promise && (await track(result));
    onAbort();
  };

  return (
    <Modal
      maxWidth={maxWidth}
      className={clsx(styles.wrapper, className)}
      showCloseButton={showCloseButton}
      close={onAbort}
    >
      {title && (
        <Box className={styles.titleContainer}>
          <Typography variant="h3" className={styles.modalTitle}>
            {title}
          </Typography>
        </Box>
      )}
      <Typography variant="body2" className={styles.confirmTextStyles}>
        {body}
      </Typography>
      <Box className={clsx(styles.buttonContainer, { [styles.fullWidth]: fullWidthButtons })}>
        {showAbortButton && (
          <Button size="small" color="primary" variant="outlined" onClick={onAbort} disabled={isPending}>
            {abortText}
          </Button>
        )}
        <Button
          size="small"
          color={confirmButtonColor}
          isLoading={isPending}
          disabled={isPending}
          variant="contained"
          onClick={onConfirmClick}
          fullWidth={!showAbortButton}
        >
          {confirmText}
        </Button>
      </Box>
    </Modal>
  );
};
